// feature/dispatcher-unified.
// Single unified Camilo dispatcher: one table with rows color-coded by
// `display_status` from the backend (verde / amarillo / gris / azul).
// Filter chip row above the table; counts visible per chip.
//
// Action column varies per status:
//   verde     → "Repartir"           (standard, no reason required)
//   amarillo  → "Repartir igualmente" (override, reason ≥10 chars required)
//   gris      → "Repartir igualmente" (override, stronger warning copy)
//   azul      → "Ver" only (already routed, read-only history)

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Spinner from "../../components/Spinner";
import CenteredModal from "../../components/CenteredModal";
import { useToast } from "../../components/Toast";
import { TipoPill, EstadoPill, ContestadaTag } from "../../components/Pills";
import { repartoInsumos, insumos as insumosApi } from "../../api/endpoints";
import CamiloCaseActionPanel from "./CamiloCaseActionPanel";
import EstadoFilterPills, { filterByActiveStatus } from "../../components/EstadoFilterPills";
import { timeAgo } from "../../components/timeAgo";

const fmtRel = (d) => d ? timeAgo(d) : "—";

// feature/insumos-enviados-export — sprint85. YYYY-MM-DD anchored to Bogota
// (UTC-5) so the default range matches the backend's Bogota date math.
const bogotaDate = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toLocaleDateString("en-CA", { timeZone: "America/Bogota" });
};

const STATUS_LABEL = {
  morado:   "Post-fallo",
  verde:    "Listos",
  amarillo: "Esperando",
  gris:     "En preparación",
  azul:     "Recientes",
};

// feature/post-fallo-routing-bridge — sprint 82. Morado bucket carries
// cases where Molly decided no-impugnar and Camilo's routing decision
// (solicitar insumo for memorial vs archivar no-action) is pending.
const STATUS_ROW_STYLE = {
  morado:   { background: "rgba(139,92,246,0.06)",  borderLeft: "3px solid #8b5cf6" },
  verde:    { background: "rgba(43,179,157,0.06)",  borderLeft: "3px solid var(--accent-mint)" },
  amarillo: { background: "rgba(217,119,6,0.06)",   borderLeft: "3px solid var(--color-warning)" },
  gris:     { background: "var(--bg-subtle)", borderLeft: "3px solid var(--text-muted)", opacity: 0.92 },
  azul:     { background: "rgba(59,130,246,0.04)",  borderLeft: "3px solid var(--color-info)", opacity: 0.85 },
};

export default function RepartoInsumosDispatcher() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("todos");
  // feature/estado-filter-pills. Default to active so the
  // dispatcher hides cerrado/contestada/devuelto rows by default.
  const [estadoFilter, setEstadoFilter] = useState("activos");
  const [searchParams, setSearchParams] = useSearchParams();
  const queryOpen = searchParams.get("open");
  const [openId, setOpenId] = useState(queryOpen ? Number(queryOpen) : null);
  useEffect(() => { if (queryOpen) setOpenId(Number(queryOpen)); }, [queryOpen]);
  function closePanel() {
    setOpenId(null);
    if (queryOpen) { searchParams.delete("open"); setSearchParams(searchParams, { replace: true }); }
  }

  function load() {
    setData(null); setError(null);
    repartoInsumos.queue()
      .then((r) => setData(r.data?.cases || []))
      .catch((e) => setError(e?.response?.data?.error || "No se pudo cargar la cola."));
  }
  useEffect(() => { load(); }, []);

  // Sprint 55c — apply the active/closed slice first, then the existing
  // pipeline-tab filter operates on that subset. Pipeline-tab counts
  // reflect the active/closed view so Camilo doesn't get a misleading
  // "Listos (12)" when 4 of those are actually cerrado.
  const estadoFiltered = useMemo(
    () => filterByActiveStatus(data || [], estadoFilter),
    [data, estadoFilter]
  );

  const counts = useMemo(() => {
    const c = { todos: 0, morado: 0, verde: 0, amarillo: 0, gris: 0, azul: 0 };
    for (const row of estadoFiltered) {
      c.todos += 1;
      if (row.display_status && c[row.display_status] != null) c[row.display_status] += 1;
    }
    return c;
  }, [estadoFiltered]);

  // sprint82 — bulk selection lives only on the morado bucket; clearing
  // happens automatically when filter or data changes.
  const [selected, setSelected] = useState(new Set());
  useEffect(() => { setSelected(new Set()); }, [filter, data]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // "archive" | "route"
  const toast = useToast();

  // feature/insumos-enviados-export — sprint85. Team-wide enviados Excel.
  // Defaults to the last 30 days; Camilo can widen/narrow before downloading.
  const [dateFrom, setDateFrom] = useState(() => bogotaDate(-30));
  const [dateTo, setDateTo] = useState(() => bogotaDate(0));
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadExcel() {
    setDownloading(true);
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const res = await insumosApi.exportEnviados(params);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="(.+)"/);
      const filename = match ? match[1] : "insumos_enviados.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.push("No se pudo generar el Excel. Intente de nuevo.", "error");
    } finally {
      setDownloading(false);
    }
  }

  async function runBulk(actionKey) {
    setBulkBusy(true);
    const ids = Array.from(selected);
    try {
      const r = actionKey === "archive"
        ? await repartoInsumos.postFalloBulkArchive(ids)
        : await repartoInsumos.postFalloBulkRouteToInsumos(ids);
      const ok = (r.data?.successes || []).length;
      const failed = (r.data?.failures || []).length;
      let msg = actionKey === "archive"
        ? `${ok} caso(s) archivados.`
        : `${ok} caso(s) enviados a insumos.`;
      if (failed) msg += ` ${failed} no se actualizaron.`;
      toast.push(msg);
      setBulkConfirm(null);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error en la acción masiva", "error");
    } finally {
      setBulkBusy(false);
    }
  }

  async function runSingle(actionKey, caseId, caseNumber) {
    try {
      const r = actionKey === "archive"
        ? await repartoInsumos.postFalloArchive(caseId)
        : await repartoInsumos.postFalloRouteToInsumos(caseId);
      toast.push(actionKey === "archive"
        ? `Caso ${caseNumber} archivado.`
        : `Caso ${caseNumber} enviado a insumos.`);
      load();
      return r;
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al actuar", "error");
      throw e;
    }
  }

  const filtered = useMemo(() => {
    if (filter === "todos") return estadoFiltered;
    return estadoFiltered.filter((r) => r.display_status === filter);
  }, [estadoFiltered, filter]);

  if (error) {
    return <div className="card tight" style={{ padding: 16, color: "var(--color-danger)" }}>{error}</div>;
  }
  if (!data) {
    return <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>;
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0 }}>Reparto de insumos</h1>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Flujo completo: insumos en preparación, esperando Previsora, listos para reparto y los repartidos recientes.
          </div>
        </div>

        {/* feature/insumos-enviados-export — sprint85. Period picker (default
            last 30 days) + team-wide enviados Excel download. The export
            covers every solicitud the equipo de insumos ha enviado
            (Natali/Cesar son quienes envían), no solo lo de un usuario. */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
          <label className="col" style={{ gap: 2, fontSize: 11, color: "var(--text-muted)" }}>
            Desde
            <input type="date" value={dateFrom} max={dateTo || undefined}
                   onChange={(e) => setDateFrom(e.target.value)}
                   style={{ padding: "4px 8px", fontSize: 12 }} />
          </label>
          <label className="col" style={{ gap: 2, fontSize: 11, color: "var(--text-muted)" }}>
            Hasta
            <input type="date" value={dateTo} min={dateFrom || undefined}
                   onChange={(e) => setDateTo(e.target.value)}
                   style={{ padding: "4px 8px", fontSize: 12 }} />
          </label>
          <button className="secondary" onClick={handleDownloadExcel} disabled={downloading}
                  style={{ whiteSpace: "nowrap" }}>
            {downloading ? "Generando…" : "Descargar Excel"}
          </button>
        </div>
      </header>

      {/* feature/estado-filter-pills. Sits above the
          pipeline tabs; counts come from the unfiltered queue so Camilo
          can see how much closed/contestada exists in his pipeline. */}
      <EstadoFilterPills cases={data || []} value={estadoFilter} onChange={setEstadoFilter} />

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Chip label="Todos"          value="todos"    current={filter} setter={setFilter} count={counts.todos} />
        <Chip label="Post-fallo"     value="morado"   current={filter} setter={setFilter} count={counts.morado}   color="purple" />
        <Chip label="Listos"         value="verde"    current={filter} setter={setFilter} count={counts.verde}    color="mint" />
        <Chip label="Esperando"      value="amarillo" current={filter} setter={setFilter} count={counts.amarillo} color="warn" />
        <Chip label="En preparación" value="gris"     current={filter} setter={setFilter} count={counts.gris}     color="muted" />
        <Chip label="Recientes"      value="azul"     current={filter} setter={setFilter} count={counts.azul}     color="blue" />
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {filtered.length === 0 ? (
          <div className="muted" style={{ padding: 32, textAlign: "center", fontSize: 13 }}>
            {filter === "todos" ? "No hay casos en el flujo." : `No hay casos en "${STATUS_LABEL[filter] || filter}".`}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                {filter === "morado" && (
                  <th style={{ width: 32 }}>
                    <input type="checkbox"
                           checked={selected.size > 0 && selected.size === filtered.length}
                           onChange={(e) =>
                             setSelected(e.target.checked
                               ? new Set(filtered.map((r) => r.id))
                               : new Set())} />
                  </th>
                )}
                <th>Caso</th>
                <th>Radicado</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th>Contestada</th>
                <th>Accionante</th>
                <th>{filter === "morado" ? "Motivo de no impugnar" : "Equipo / Abogado"}</th>
                <th>Tiempo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <DispatcherRow key={c.id} row={c}
                               filter={filter}
                               selected={selected.has(c.id)}
                               onToggleSelect={() => setSelected(prev => {
                                 const next = new Set(prev);
                                 if (next.has(c.id)) next.delete(c.id);
                                 else next.add(c.id);
                                 return next;
                               })}
                               onOpen={() => setOpenId(c.id)}
                               onPostFalloAction={runSingle} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId && (
        <CamiloCaseActionPanel
          caseRow={data.find((row) => row.id === openId) || { id: openId }}
          open={!!openId}
          onClose={closePanel}
          onCaseUpdate={() => { load(); }}
        />
      )}

      {/* sprint82 — bulk-action bar for post-fallo */}
      {filter === "morado" && selected.size > 0 && (
        <div style={{
          position: "sticky", bottom: 16, alignSelf: "center",
          background: "var(--bg-card)",
          border: "1px solid #8b5cf6",
          borderRadius: 8, padding: "10px 16px",
          display: "flex", gap: 12, alignItems: "center",
          boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
        }}>
          <strong>{selected.size}</strong> seleccionado(s)
          <button onClick={() => setBulkConfirm("route")} disabled={bulkBusy}>
            Solicitar insumo ({selected.size})
          </button>
          <button onClick={() => setBulkConfirm("archive")} disabled={bulkBusy}
                  style={{ background: "var(--color-danger)", color: "var(--on-accent)", borderColor: "var(--color-danger)" }}>
            Archivar — no requiere acción ({selected.size})
          </button>
          <button className="ghost sm" onClick={() => setSelected(new Set())} disabled={bulkBusy}>
            Limpiar
          </button>
        </div>
      )}

      {bulkConfirm && (
        <CenteredModal
          open
          onClose={bulkBusy ? undefined : () => setBulkConfirm(null)}
          title={bulkConfirm === "archive"
            ? `¿Archivar ${selected.size} casos sin acción?`
            : `¿Enviar ${selected.size} casos a insumos?`}
          size="sm"
          footer={<>
            <button className="secondary" disabled={bulkBusy} onClick={() => setBulkConfirm(null)}>Cancelar</button>
            <button disabled={bulkBusy} onClick={() => runBulk(bulkConfirm)}>
              {bulkBusy && <Spinner color="currentColor" />} Confirmar
            </button>
          </>}
        >
          <p style={{ margin: 0, fontSize: 13 }}>
            {bulkConfirm === "archive"
              ? "Esto archivará los casos seleccionados. La decisión queda en el registro de auditoría con su autoría."
              : "Esto enviará los casos al equipo de insumos para memorial de cumplimiento."}
          </p>
        </CenteredModal>
      )}
    </div>
  );
}

function Chip({ label, value, current, setter, count, color }) {
  const active = current === value;
  const palette = {
    mint:   { bg: "rgba(43,179,157,0.12)", fg: "var(--accent-mint)" },
    warn:   { bg: "rgba(217,119,6,0.12)",  fg: "var(--color-warning-ink)" },
    muted:  { bg: "var(--bg-subtle)", fg: "var(--text-muted)" },
    blue:   { bg: "rgba(59,130,246,0.12)", fg: "var(--color-info)" },
    purple: { bg: "rgba(139,92,246,0.12)", fg: "#6d28d9" },
  };
  const c = palette[color] || palette.muted;
  return (
    <button type="button" onClick={() => setter(value)}
            className={active ? "" : "secondary"}
            style={{
              padding: "4px 10px", fontSize: 12,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
      <span>{label}</span>
      {count > 0 && (
        <span style={{
          background: active ? "rgba(255,255,255,0.25)" : c.bg,
          color: active ? "var(--on-accent)" : c.fg,
          fontSize: 10, fontWeight: 600,
          padding: "1px 6px", borderRadius: 10,
          minWidth: 16, textAlign: "center",
        }}>{count}</span>
      )}
    </button>
  );
}

function DispatcherRow({ row, onOpen, filter, selected, onToggleSelect, onPostFalloAction }) {
  const rowStyle = STATUS_ROW_STYLE[row.display_status] || {};
  const days = row.days_waiting;
  const stale = (days || 0) >= 7;
  const isMorado = row.display_status === "morado";
  const showCheckbox = filter === "morado" && isMorado;
  // For morado rows, show the motivo (por_que_no_impugno) instead of
  // team/lawyer — those don't exist on a post-fallo case yet.
  const teamOrLawyer = isMorado
    ? (row.post_fallo_motivo
        ? <em style={{ color: "var(--text-muted)", fontSize: 12 }}>{row.post_fallo_motivo}</em>
        : <span className="muted">—</span>)
    : row.display_status === "verde"
      ? (row.abogado_responsable_name || row.abogado_responsable_email || "Sin abogado")
      : row.display_status === "azul"
        ? (row.abogado_responsable_name || row.abogado_responsable_email || "—")
        : (row.insumos_user_name || row.insumos_user_email || "—");
  const timeText = isMorado
    ? `Molly decidió ${fmtRel(row.pending_post_fallo_routing_at)}`
    : row.display_status === "azul"
      ? `repartido ${fmtRel(row.lawyer_routed_at)}`
      : row.display_status === "verde"
        ? `recibido ${fmtRel(row.insumos_received_at)}`
        : days != null
          ? `${days} ${days === 1 ? "día" : "días"}`
          : fmtRel(row.created_at);

  return (
    <tr style={rowStyle}>
      {filter === "morado" && (
        <td onClick={(e) => e.stopPropagation()}>
          {showCheckbox && (
            <input type="checkbox" checked={selected} onChange={onToggleSelect}
                   aria-label={`Seleccionar ${row.case_number}`} />
          )}
        </td>
      )}
      <td>{row.case_number}</td>
      <td style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: 11, color: "var(--text-muted)" }}>
        {row.radicado || "—"}
      </td>
      <td><TipoPill tipo={row.tipo} /></td>
      <td><EstadoPill c={row} /></td>
      <td><ContestadaTag contestadaAt={row.contestada_at} estado={row.estado} dateFormat="short" /></td>
      <td>{row.accionante || "—"}</td>
      <td>{teamOrLawyer}</td>
      <td style={stale ? { color: "var(--color-warning-ink)", fontWeight: 600 } : undefined}>{timeText}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        {isMorado ? (
          <PostFalloActions row={row} onAction={onPostFalloAction} onOpen={onOpen} />
        ) : (
          <VerActuarButton row={row} onOpen={onOpen} />
        )}
      </td>
    </tr>
  );
}

// sprint82 — per-row post-fallo actions. Two small buttons + a "Ver"
// fallback that opens the standard Camilo panel for context.
function PostFalloActions({ row, onAction, onOpen }) {
  const [busy, setBusy] = useState(false);
  async function go(actionKey) {
    setBusy(true);
    try { await onAction(actionKey, row.id, row.case_number); }
    catch (_) { /* toast handled upstream */ }
    finally { setBusy(false); }
  }
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      <button className="sm" disabled={busy}
              onClick={() => go("route")}
              title="Enviar a insumos para memorial de cumplimiento">
        Solicitar insumo
      </button>
      <button className="ghost sm" disabled={busy}
              onClick={() => go("archive")}
              title="Archivar — no requiere acción adicional">
        Archivar
      </button>
      <button className="ghost sm" disabled={busy}
              onClick={onOpen}
              style={{ fontSize: 11 }}
              title="Ver detalle del caso">
        Ver
      </button>
    </span>
  );
}

// feature/camilo-action-panel. Single per-row trigger that
// replaces the Sprint 37c-40 "Asignar abogado" / "Repartir igualmente"
// per-state buttons. The state-appropriate actions live inside the
// CamiloCaseActionPanel (mounted by the dispatcher itself) so a row
// re-render doesn't tear down the panel.
const NO_ACTION_STATES = new Set(["cerrado", "devuelto", "devuelto_a_reparto", "error_marcado"]);
function VerActuarButton({ row, onOpen }) {
  const noAction = NO_ACTION_STATES.has(row.estado);
  return (
    <button
      className={noAction ? "ghost sm" : "secondary sm"}
      onClick={onOpen}
      disabled={noAction}
      title={noAction ? "Sin acciones disponibles para este estado" : "Ver detalles y actuar"}
      style={{ whiteSpace: "nowrap" }}
    >
      Ver / actuar
    </button>
  );
}

// feature/camilo-action-panel.
// The old per-row ActionButton (Sprint 37c "Repartir igualmente" + Sprint
// 40 "Asignar abogado" inline modals) was removed. All those flows now
// live inside CamiloCaseActionPanel, branched by case estado:
//   pendiente_reparto_lawyer + no abogado → AssignLawyerZone (assign first)
//   pendiente_reparto_lawyer + has abogado → AssignLawyerZone (route)
//   insumos_solicitados / expediente_solicitado / insumos_recibidos
//                                                → UploadResponseZone
//                                                  (upload + override path)
//   asignado_abogado / cerrado / devuelto*       → read-only fallback
// The dispatcher row only fires VerActuarButton, which opens the panel.
